/* eslint-disable react/no-unescaped-entities */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import {
  ChevronRight,
  Check,
  BookOpen,
  AlertCircle,
  MessageCircle,
  Compass,
  UserCheck,
  Lock,
  Users,
  FileText,
  Coins,
  X,
  Sparkles,
} from 'lucide-react';
import { HOME } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import NotaryDocumentPreview from '@/components/NotaryDocumentPreview';

const HomePage = () => {
  const navigate = useNavigate();
  const [isHovering, setIsHovering] = useState(false);

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <SEO
        title="Comprendre vos documents notariés simplement | HÉRITA"
        description="Comprenez facilement vos documents notariés grâce à l'intelligence artificielle. Succession, donation, testament, vente immobilière, attestation immobilière et bien plus."
        path="/"
        ogTitle="HÉRITA — IA d'analyse des documents notariés"
        ogDescription="Transformez vos actes notariés complexes en explications claires, personnalisées et compréhensibles en quelques minutes."
      />
      <main id="main">
      {/* Hero Section */}
      <section
        data-testid={HOME.heroSection}
        aria-label="Présentation de Hérita"
        className="relative overflow-hidden pt-14 sm:pt-20 pb-16 sm:pb-24 px-5 sm:px-6 md:px-12 lg:px-24"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/03884dd5-fbbf-4e16-a56d-37b6141ad5dd/images/061c56843ef2ab3d3802e278ebc9f7b2eb81f452bbd0b3a28adfd83c70723c72.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-3xl mx-auto text-center relative z-10">
          {/* Discrete top mark */}
          <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-[#2C363F] mb-8 sm:mb-12 opacity-70">
            IA française pour comprendre les documents notariés
          </p>

          <h1
            className="font-['Cormorant_Garamond'] font-light text-[40px] leading-[1.08] sm:text-[60px] lg:text-[70px] text-[#1A2530] mb-6 sm:mb-8"
            style={{ letterSpacing: '-0.015em' }}
          >
            Votre notaire rédige l'acte.<br className="hidden sm:block" /> Hérita vous l'explique.
          </h1>
          <p className="font-['Work_Sans'] font-light text-base sm:text-lg text-[#2C363F] mb-10 sm:mb-14 max-w-2xl mx-auto leading-relaxed">
            Hérita identifie automatiquement les personnes, les biens et les
            montants, puis explique ce que le document change concrètement
            pour vous, dans un langage simple et compréhensible.
          </p>

          {/* Upload Box */}
          <div
            className="bg-white/95 backdrop-blur-xl rounded-2xl p-7 sm:p-9 shadow-[0_20px_60px_-25px_rgba(50,35,20,0.25)] transition-shadow duration-500 mb-4 border border-[#E2DBCF]/50"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="flex flex-col items-center justify-center">
              <div className="mb-5">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/03884dd5-fbbf-4e16-a56d-37b6141ad5dd/images/b3fe73767ebf6b738a6984c45ca3a582ac6fa7b2bf2b1b144d876065fb81557c.png"
                  alt="Document notarié — illustration"
                  width="112"
                  height="112"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  className="w-24 h-24 sm:w-28 sm:h-28 object-contain"
                  style={{
                    transform: isHovering ? 'translateY(-6px)' : 'translateY(0)',
                    transition: 'transform 0.4s ease-out',
                  }}
                />
              </div>
              <p className="font-['Cormorant_Garamond'] font-medium text-2xl sm:text-[26px] text-[#1A2530] mb-2 leading-tight">
                Ajoutez votre document notarié
              </p>
              <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] mb-5 max-w-md leading-relaxed">
                Importez votre acte ou courrier notarial. Hérita identifie les
                informations importantes et vous explique ce qui vous concerne.
              </p>
              <Button
                data-testid={HOME.uploadButton}
                onClick={() => navigate('/upload')}
                className="bg-[#1A2530] hover:bg-[#2C363F] text-white px-9 py-5 rounded-full font-['Work_Sans'] font-medium text-[15px] transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Analyser mon document
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>

              {/* Trust line — directly under the primary button, more visible */}
              <div
                className="mt-5 flex items-center gap-2"
                data-testid="hero-trust-line"
              >
                <Lock className="h-3.5 w-3.5 text-[#1A2530] opacity-80" />
                <p className="font-['Work_Sans'] text-[13px] sm:text-[14px] text-[#1A2530] font-medium">
                  Vos documents originaux sont supprimés après analyse.
                </p>
              </div>

              {/* 3 micro-garanties sobres — clarté, confiance, conversion */}
              <div
                className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5"
                data-testid="hero-micro-guarantees"
              >
                <span className="inline-flex items-center gap-1.5 font-['Work_Sans'] text-[12px] sm:text-[12.5px] text-[#2C363F] opacity-90">
                  <Check className="h-3 w-3 text-[#1A2530]" strokeWidth={3} />
                  Analyse gratuite au départ
                </span>
                <span className="inline-flex items-center gap-1.5 font-['Work_Sans'] text-[12px] sm:text-[12.5px] text-[#2C363F] opacity-90">
                  <Check className="h-3 w-3 text-[#1A2530]" strokeWidth={3} />
                  Aucun compte requis
                </span>
                <span className="inline-flex items-center gap-1.5 font-['Work_Sans'] text-[12px] sm:text-[12.5px] text-[#2C363F] opacity-90">
                  <Check className="h-3 w-3 text-[#1A2530]" strokeWidth={3} />
                  Documents supprimés après analyse
                </span>
              </div>

              <button
                onClick={() => navigate('/upload')}
                className="mt-4 font-['Work_Sans'] text-[13px] text-[#2C363F] hover:text-[#1A2530] underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530] transition-colors"
              >
                ou importez directement un fichier
              </button>
            </div>
          </div>

          {/* Alternative entry point — free diagnostic for visitors who
              don't have a document yet. Sober pill-card below the upload
              box, same width, premium ivory tone, sets up the new funnel
              without competing visually with the primary CTA. */}
          <div
            className="mt-4 mb-7 bg-[#FDFBF7] border border-[#E2DBCF] rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 text-left"
            data-testid="diagnostic-entry-card"
          >
            <div className="flex-1 min-w-0">
              <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-75 mb-1.5">
                Vous ne savez pas par où commencer ?
              </p>
              <p className="font-['Cormorant_Garamond'] font-medium text-[20px] sm:text-[24px] text-[#1A2530] leading-tight mb-1.5">
                Faites un diagnostic gratuit
              </p>
              <p className="font-['Work_Sans'] text-[13px] sm:text-[13.5px] text-[#2C363F] opacity-90 leading-relaxed">
                Répondez à quelques questions et découvrez les points
                importants de votre situation en 3 minutes.
              </p>
            </div>
            <Button
              data-testid="diagnostic-entry-cta"
              onClick={() => {
                // GA4 — diagnostic_cta_clicked from the home hero
                import('@/lib/analytics').then((m) => m.trackDiagnosticCtaClicked?.('home_hero'));
                navigate('/diagnostic');
              }}
              className="bg-white hover:bg-[#1A2530] hover:text-white text-[#1A2530] border border-[#1A2530] px-6 py-4 rounded-full font-['Work_Sans'] font-medium text-[14px] transition-all duration-300 self-start sm:self-auto whitespace-nowrap"
            >
              Commencer le diagnostic
              <ChevronRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>

          {/* Supported documents — sober elegant badges, reassures immediately on coverage */}
          <div className="mb-7" data-testid="supported-documents">
            <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-70 mb-3">
              Documents pris en charge
            </p>
            <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 max-w-2xl mx-auto">
              {[
                { label: 'Donation-partage', to: '/donation-partage#definition' },
                { label: 'Succession', to: '/declaration-succession#definition' },
                { label: 'Testament', to: '/testament#definition' },
                { label: 'Attestation immobilière', to: '/attestation-immobiliere#definition' },
                { label: 'Vente immobilière', to: '/acte-de-vente#definition' },
                { label: 'Partage', to: '/indivision#definition' },
                { label: 'Indivision', to: '/indivision#definition' },
                { label: 'SCI', to: '/sci#definition' },
                { label: 'Courrier notarial', to: '/courrier-notarial#definition' },
              ].map(({ label, to }) => (
                <Link
                  key={label}
                  to={to}
                  data-testid={`supported-badge-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="inline-flex items-center font-['Work_Sans'] text-[12px] sm:text-[12.5px] text-[#1A2530] bg-white/85 border border-[#E2DBCF] rounded-full px-3 py-1 leading-none hover:bg-white hover:border-[#1A2530] hover:shadow-sm transition-all"
                  aria-label={`Comprendre le document : ${label}`}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Compact example result card */}
          <Card
            data-testid="example-result-card"
            className="bg-white/95 backdrop-blur-xl rounded-2xl p-5 sm:p-6 border border-[#E2DBCF]/60 shadow-sm max-w-2xl mx-auto text-left"
          >
            <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-70 mb-3 text-center">
              Exemple d'analyse
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="text-center sm:text-left">
                <div className="w-7 h-7 rounded-full bg-[#E3D5CA]/60 flex items-center justify-center mx-auto sm:mx-0 mb-2">
                  <FileText className="h-3.5 w-3.5 text-[#1A2530]" />
                </div>
                <p className="font-['Cormorant_Garamond'] font-semibold text-[#1A2530] text-[15px] sm:text-[16px] leading-tight">
                  Donation-partage
                </p>
                <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.16em] text-[#2C363F] opacity-70 mt-1">
                  Document détecté
                </p>
              </div>
              <div className="text-center sm:text-left">
                <div className="w-7 h-7 rounded-full bg-[#E3D5CA]/60 flex items-center justify-center mx-auto sm:mx-0 mb-2">
                  <Users className="h-3.5 w-3.5 text-[#1A2530]" />
                </div>
                <p className="font-['Cormorant_Garamond'] font-semibold text-[#1A2530] text-[20px] sm:text-[22px] leading-none">
                  6
                </p>
                <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.16em] text-[#2C363F] opacity-70 mt-1">
                  Personnes identifiées
                </p>
              </div>
              <div className="text-center sm:text-left">
                <div className="w-7 h-7 rounded-full bg-[#E3D5CA]/60 flex items-center justify-center mx-auto sm:mx-0 mb-2">
                  <FileText className="h-3.5 w-3.5 text-[#1A2530]" />
                </div>
                <p className="font-['Cormorant_Garamond'] font-semibold text-[#1A2530] text-[20px] sm:text-[22px] leading-none">
                  2
                </p>
                <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.16em] text-[#2C363F] opacity-70 mt-1">
                  Biens identifiés
                </p>
              </div>
              <div className="text-center sm:text-left">
                <div className="w-7 h-7 rounded-full bg-[#E3D5CA]/60 flex items-center justify-center mx-auto sm:mx-0 mb-2">
                  <Coins className="h-3.5 w-3.5 text-[#1A2530]" />
                </div>
                <p className="font-['Cormorant_Garamond'] font-semibold text-[#1A2530] text-[16px] sm:text-[18px] leading-tight">
                  94 000 €
                </p>
                <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.16em] text-[#2C363F] opacity-70 mt-1">
                  Valeur détectée
                </p>
              </div>
            </div>
            <div className="pt-3 border-t border-[#E2DBCF] flex flex-col items-center gap-1.5">
              <div className="flex items-center gap-2">
                <UserCheck className="h-3.5 w-3.5 text-[#1A2530]" />
                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#1A2530]">
                  Lecture personnalisée disponible
                </p>
              </div>
              <p
                className="font-['Work_Sans'] text-[11px] sm:text-[12px] text-[#2C363F] italic opacity-80 leading-snug"
                data-testid="premium-pricing-hint"
              >
                Lecture complète à partir de 19 € — paiement unique.
              </p>
            </div>
          </Card>

          {/* Reassurance line */}
          <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] italic mt-6 max-w-xl mx-auto leading-relaxed opacity-90">
            Hérita ne remplace pas votre notaire. Il vous aide à comprendre ce que votre document
            implique concrètement pour vous.
          </p>
        </div>
      </section>

      {/* Trust Section — outcome-driven feature cards */}
      <section
        data-testid={HOME.trustSection}
        className="py-16 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                Icon: BookOpen,
                title: 'Explications claires',
                detail: 'Comprendre ce que votre document signifie réellement.',
              },
              {
                Icon: AlertCircle,
                title: 'Conséquences identifiées',
                detail: 'Repérer vos droits, obligations, montants et points importants.',
              },
              {
                Icon: MessageCircle,
                title: 'Questions pour votre notaire',
                detail: 'Préparer les bonnes questions avant votre rendez-vous.',
              },
              {
                Icon: Compass,
                title: 'Prochaines démarches',
                detail: "Savoir précisément quoi faire ensuite, dans le bon ordre.",
              },
              {
                Icon: UserCheck,
                title: 'Lecture personnalisée',
                detail: 'Comprendre ce qui vous concerne personnellement dans le document.',
              },
            ].map(({ Icon, title, detail }, idx) => (
              <Card
                key={idx}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300 border border-[#E2DBCF]"
                data-testid={`feature-card-${idx}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#E3D5CA] rounded-full flex items-center justify-center mb-5">
                    <Icon className="h-6 w-6 text-[#1A2530]" />
                  </div>
                  <h3 className="font-['Work_Sans'] font-semibold text-[17px] sm:text-[18px] text-[#1A2530] mb-3 tracking-tight">
                    {title}
                  </h3>
                  <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                    {detail}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section
        data-testid={HOME.howItWorksSection}
        className="py-16 sm:py-20 px-5 sm:px-6 md:px-12 lg:px-24 bg-white"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 sm:mb-14">
            <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-[#2C363F] mb-4 opacity-70">
              En trois temps
            </p>
            <h2
              className="font-['Cormorant_Garamond'] font-light text-[34px] sm:text-[44px] lg:text-[52px] text-[#1A2530] leading-[1.15]"
              style={{ letterSpacing: '-0.01em' }}
            >
              Comment cela se passe
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#E3D5CA] rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="font-['IBM_Plex_Mono'] text-2xl text-[#1A2530] font-medium">01</span>
              </div>
              <h3 className="font-['Cormorant_Garamond'] font-medium text-[24px] sm:text-[26px] text-[#1A2530] mb-2 leading-tight">
                Importez
              </h3>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                Déposez votre document notarié en quelques secondes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#E3D5CA] rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="font-['IBM_Plex_Mono'] text-2xl text-[#1A2530] font-medium">02</span>
              </div>
              <h3 className="font-['Cormorant_Garamond'] font-medium text-[24px] sm:text-[26px] text-[#1A2530] mb-2 leading-tight">
                Analyse
              </h3>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                Hérita identifie les personnes, les montants, les biens et les clauses importantes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#E3D5CA] rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="font-['IBM_Plex_Mono'] text-2xl text-[#1A2530] font-medium">03</span>
              </div>
              <h3 className="font-['Cormorant_Garamond'] font-medium text-[24px] sm:text-[26px] text-[#1A2530] mb-2 leading-tight">
                Comprenez
              </h3>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                Recevez une lecture claire, personnalisée et les démarches à effectuer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why HÉRITA over ChatGPT — direct objection handling */}
      <section
        data-testid="why-not-chatgpt-section"
        aria-labelledby="why-not-chatgpt-heading"
        className="py-16 sm:py-20 px-5 sm:px-6 md:px-12 lg:px-24 bg-white border-t border-[#E2DBCF]"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 sm:mb-14">
            <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-[#2C363F] mb-4 opacity-70">
              Objection fréquente
            </p>
            <h2
              id="why-not-chatgpt-heading"
              className="font-['Cormorant_Garamond'] font-light text-[32px] sm:text-[42px] lg:text-[50px] text-[#1A2530] mb-5 leading-[1.15]"
              style={{ letterSpacing: '-0.01em' }}
            >
              Pourquoi ne pas simplement utiliser ChatGPT&nbsp;?
            </h2>
            <p className="font-['Work_Sans'] text-[15px] sm:text-base text-[#2C363F] max-w-2xl mx-auto leading-relaxed">
              ChatGPT sait lire un document. HÉRITA a été conçu spécifiquement
              pour comprendre les actes notariés et vous aider à en tirer les
              conséquences concrètes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-7">
            {/* ChatGPT — neutral / cream card */}
            <article
              data-testid="comparison-chatgpt"
              className="bg-[#F9F7F3] border border-[#E2DBCF] rounded-2xl p-7 sm:p-9"
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#2C363F] opacity-70 mb-3">
                Outil généraliste
              </p>
              <h3 className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[30px] text-[#1A2530] mb-6 leading-tight">
                ChatGPT
              </h3>
              <ul className="space-y-3.5 list-none p-0">
                {[
                  'Outil généraliste',
                  'Nécessite de savoir quoi demander',
                  'Réponses variables selon le prompt utilisé',
                  'Pas de lecture adaptée à votre situation personnelle',
                  'Pas de questions préparées pour le notaire',
                  'Pas de prochaines démarches structurées',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-white border border-[#D3CCBE] flex items-center justify-center flex-shrink-0">
                      <X className="h-3 w-3 text-[#2C363F] opacity-70" strokeWidth={2.5} aria-hidden="true" />
                    </span>
                    <span className="font-['Work_Sans'] text-[14.5px] text-[#2C363F] leading-snug">
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
            </article>

            {/* HÉRITA — premium highlighted card */}
            <article
              data-testid="comparison-herita"
              className="bg-white border-t-2 border-t-[#1A2530] border-x border-b border-[#E2DBCF] rounded-2xl p-7 sm:p-9 shadow-md"
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#1A2530] mb-3 inline-flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" aria-hidden="true" />
                Conçu pour les documents notariés
              </p>
              <h3 className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[30px] text-[#1A2530] mb-6 leading-tight">
                HÉRITA
              </h3>
              <ul className="space-y-3.5 list-none p-0">
                {[
                  'Conçu pour les successions, donations et actes notariés',
                  'Analyse guidée automatiquement',
                  'Comprend les notions de succession, usufruit, indivision et donation',
                  'Lecture personnalisée selon votre situation',
                  'Questions à poser au notaire générées automatiquement',
                  'Prochaines démarches et points d\'attention identifiés',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#1A2530] flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-white" strokeWidth={3} aria-hidden="true" />
                    </span>
                    <span className="font-['Work_Sans'] text-[14.5px] text-[#1A2530] leading-snug">
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <p className="mt-10 sm:mt-12 font-['Work_Sans'] text-[13.5px] sm:text-[14px] text-[#2C363F] italic text-center opacity-90 max-w-2xl mx-auto leading-relaxed">
            HÉRITA ne remplace ni votre notaire ni une intelligence artificielle
            généraliste. Il a été conçu pour aider les particuliers à
            comprendre rapidement les documents notariés et leurs conséquences
            concrètes.
          </p>
        </div>
      </section>

      {/* Before / After — transformation, not metadata */}
      <section
        data-testid={HOME.exampleAnalysisSection}
        className="py-16 sm:py-20 px-5 sm:px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-14">
            <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-[#2C363F] mb-4 opacity-70">
              Avant · Après
            </p>
            <h2
              className="font-['Cormorant_Garamond'] font-light text-[34px] sm:text-[44px] lg:text-[52px] text-[#1A2530] mb-5 leading-[1.15]"
              style={{ letterSpacing: '-0.01em' }}
            >
              Ce que votre notaire écrit.<br className="hidden sm:block" /> Ce que cela change pour vous.
            </h2>
            <p className="font-['Work_Sans'] text-[15px] sm:text-base text-[#2C363F] max-w-xl mx-auto leading-relaxed">
              À gauche, le document tel que vous le recevez. À droite, ce que Hérita
              vous explique clairement.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start mb-10">
            {/* Left: Realistic notary document */}
            <div>
              <p className="font-['Work_Sans'] text-xs uppercase tracking-widest text-[#2C363F] mb-4 text-center lg:text-left">
                Le document tel que vous le recevez
              </p>
              <NotaryDocumentPreview compact={true} />
            </div>

            {/* Right: Outcome card — visually stronger to immediately convey the value */}
            <div className="lg:pt-6">
              <p className="font-['Work_Sans'] text-xs uppercase tracking-widest text-[#2C363F] mb-4 text-center lg:text-left">
                Ce que Hérita vous explique
              </p>
              <div
                className="bg-white rounded-2xl p-8 sm:p-9 shadow-xl border-t-2 border-t-[#1A2530] border-x border-b border-[#E2DBCF]"
                data-testid="example-outcome-card"
              >
                <div className="mb-6 pb-5 border-b border-[#E2DBCF]">
                  <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 mb-2">
                    Lecture personnalisée — exemple
                  </p>
                  <h3 className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[30px] text-[#1A2530] leading-[1.15]">
                    Ce que cela signifie pour vous
                  </h3>
                </div>

                <ul className="space-y-3.5 mb-6">
                  {[
                    'Vous devenez plein propriétaire du bien.',
                    'Aucun droit de donation identifié.',
                    'Une formalité administrative reste à effectuer.',
                    '3 points importants ont été détectés.',
                    'Questions à poser à votre notaire disponibles.',
                    'Prochaines démarches disponibles.',
                  ].map((line, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3"
                      data-testid={`outcome-line-${idx}`}
                    >
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1A2530] flex-shrink-0" />
                      <span className="font-['Work_Sans'] text-[15px] sm:text-[16px] text-[#1A2530] leading-snug">
                        {line}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="pt-4 border-t border-[#E2DBCF] flex items-center gap-2">
                  <Lock className="h-3 w-3 text-[#2C363F] opacity-70" />
                  <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80">
                    Lecture réalisée en moins de 30 secondes
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust badges — engagement */}
          <div className="bg-[#F9F7F3] rounded-2xl p-6 sm:p-8 border border-[#E2DBCF] max-w-3xl mx-auto">
            <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] mb-4 text-center">
              Notre engagement
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] mb-1">
                  Aucune invention
                </p>
                <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug">
                  Hérita explique le document fourni, sans créer d'informations
                  absentes de l'acte.
                </p>
              </div>
              <div>
                <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] mb-1">
                  Vocabulaire simple
                </p>
                <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug">
                  Aucun jargon inutile&nbsp;: chaque notion est reformulée clairement.
                </p>
              </div>
              <div>
                <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] mb-1">
                  Suppression des documents
                </p>
                <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug">
                  Les documents originaux sont supprimés après l'analyse.
                </p>
              </div>
            </div>
            <p
              className="font-['Work_Sans'] text-[12px] text-[#2C363F] italic text-center mt-5 opacity-80 leading-relaxed"
              data-testid="engagement-disclaimer"
            >
              Hérita fournit une lecture informative et ne remplace pas le conseil
              de votre notaire.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing — premium outcome-focused, no "free service" framing */}
      <section
        className="py-16 sm:py-20 px-5 sm:px-6 md:px-12 lg:px-24 bg-white"
        data-testid="pricing-section"
      >
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#F9F7F3] rounded-2xl p-8 sm:p-10 border border-[#E2DBCF] shadow-sm">
            <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-[#2C363F] mb-4 opacity-70 text-center">
              Une lecture en deux temps
            </p>
            <h2
              className="font-['Cormorant_Garamond'] font-light text-[28px] sm:text-[36px] lg:text-[40px] text-[#1A2530] leading-[1.2] text-center mb-5"
              style={{ letterSpacing: '-0.01em' }}
            >
              Commencez gratuitement.
            </h2>
            <p className="font-['Work_Sans'] text-[15px] sm:text-base text-[#2C363F] leading-relaxed text-center max-w-2xl mx-auto mb-8">
              Téléchargez votre document et découvrez immédiatement ce qu'Hérita a
              identifié&nbsp;: type d'acte, personnes concernées, biens, montants et
              premiers éléments importants. Débloquez ensuite la lecture complète
              uniquement si elle vous apporte de la valeur.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              <div
                className="p-5 bg-white rounded-xl border border-[#E2DBCF]/60"
                data-testid="pricing-card-preview"
              >
                <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] mb-2">
                  Aperçu gratuit
                </p>
                <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug">
                  Type d'acte, personnes identifiées, biens et résumé du document.
                </p>
              </div>
              <div
                className="p-5 bg-[#1A2530] text-white rounded-xl border border-[#1A2530] shadow-md"
                data-testid="pricing-card-full"
              >
                <p className="font-['Work_Sans'] font-semibold text-[14px] mb-2">
                  Lecture complète — 19&nbsp;€
                </p>
                <p className="font-['Work_Sans'] text-[12px] text-[#E3D5CA] leading-snug">
                  Votre lecture personnalisée, les conséquences pratiques, les
                  démarches à prévoir, les points de vigilance et les questions à
                  poser au notaire.
                </p>
              </div>
              <div
                className="p-5 bg-white rounded-xl border border-[#E2DBCF]/60"
                data-testid="pricing-card-onetime"
              >
                <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] mb-2">
                  Paiement unique
                </p>
                <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug">
                  Sans abonnement. Sans engagement. Vous ne payez qu'une fois pour
                  ce document.
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <Button
                data-testid="pricing-cta"
                onClick={() => navigate('/upload')}
                className="bg-[#1A2530] hover:bg-[#2C363F] text-white px-9 py-5 rounded-full font-['Work_Sans'] font-medium text-[15px] transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Analyser mon document
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        data-testid={HOME.finalCtaSection}
        className="py-20 sm:py-24 px-5 sm:px-6 md:px-12 lg:px-24 bg-[#F9F7F3]"
      >
        <div className="max-w-2xl mx-auto text-center">
          <h2
            className="font-['Cormorant_Garamond'] font-light text-[34px] sm:text-[44px] lg:text-[52px] text-[#1A2530] mb-6 leading-[1.15]"
            style={{ letterSpacing: '-0.01em' }}
          >
            Comprendre un document notarié<br className="hidden sm:block" /> ne devrait jamais être compliqué.
          </h2>
          <p className="font-['Work_Sans'] text-[15px] sm:text-base text-[#2C363F] mb-10 max-w-md mx-auto leading-relaxed">
            Comprenez simplement ce que votre document implique, ce qui vous
            concerne et les prochaines démarches à effectuer.
          </p>
          <Button
            onClick={() => navigate('/upload')}
            className="bg-[#1A2530] hover:bg-[#2C363F] text-white px-10 py-5 rounded-full font-['Work_Sans'] font-medium text-[15px] transition-all duration-300 shadow-md hover:shadow-lg"
            data-testid="final-cta-button"
          >
            Analyser mon document
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      </main>
      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-[#E2DBCF] bg-white">
        <div className="max-w-6xl mx-auto">
          <p
            className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] italic text-center mb-6 leading-relaxed opacity-90 max-w-2xl mx-auto"
            data-testid="footer-legal-disclaimer"
          >
            Les analyses sont fournies à titre informatif et ne remplacent pas le conseil de
            votre notaire.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center pt-6 border-t border-[#E2DBCF]/60">
            <p className="font-['Work_Sans'] text-sm text-[#2C363F] mb-4 md:mb-0">
              © 2026 Hérita. Tous droits réservés.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
              <Link
                to="/comprendre-document-notarie"
                className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors duration-300"
                data-testid="footer-link-bibliotheque"
              >
                Guides
              </Link>
              <Link
                to="/mentions-legales"
                className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors duration-300"
                data-testid="footer-link-mentions"
              >
                Mentions légales
              </Link>
              <Link
                to="/confidentialite"
                className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors duration-300"
                data-testid="footer-link-confidentialite"
              >
                Confidentialité &amp; sécurité
              </Link>
              <Link
                to="/cgu"
                className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors duration-300"
                data-testid="footer-link-cgu"
              >
                CGU
              </Link>
              <Link
                to="/contact"
                className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors duration-300"
                data-testid="footer-link-contact"
              >
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
